import React, { useEffect, useState } from "react";
import { Bell, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Switch } from "../components/ui/switch";

const CHANNELS = [
  {
    key: "invoice_status_email",
    title: "Invoice decisions",
    body: "Email me when an invoice is approved, paid, rejected or returned to review.",
  },
  {
    key: "err_adjustment_email",
    title: "Earned refund changes",
    body: "Email me when my earned refund balance is credited or corrected.",
  },
  {
    key: "approval_reminder_email",
    title: "Approval reminders",
    body: "Email me a daily nudge when invoices have been waiting more than two days for my approval.",
  },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/me/notification-preferences")
      .then(({ data }) => setPrefs(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const toggle = async (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    setSaving(true);
    try {
      const { data } = await api.put("/me/notification-preferences", next);
      setPrefs(data);
      toast.success("Notice preferences saved");
    } catch (error) {
      setPrefs(prefs);
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-[900px] px-6 lg:px-10 py-10 md:py-14" data-testid="settings-page">
      <div className="mb-10">
        <div className="eyebrow mb-2">Your account</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl leading-none tracking-tight" style={{ color: "var(--ink)" }}>
          Settings
        </h1>
        <p className="mt-3 text-base max-w-xl" style={{ color: "var(--muted)" }}>
          Choose which updates reach your inbox. Everything still appears in your portal notices,
          so you never miss a decision.
        </p>
      </div>

      <div className="border" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <div className="px-6 py-5 border-b flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
          <Mail size={14} style={{ color: "var(--muted)" }}/>
          <span className="eyebrow">Email notices</span>
        </div>
        {!prefs && <div className="px-6 py-10 text-sm" style={{ color: "var(--muted)" }} data-testid="settings-loading">Loading preferences…</div>}
        {prefs && CHANNELS.filter((channel) => (
          user?.invoice_service !== false
          || !["invoice_status_email", "approval_reminder_email"].includes(channel.key)
        )).map((channel) => (
          <div
            key={channel.key}
            className="px-6 py-5 border-b last:border-b-0 flex items-start justify-between gap-6"
            style={{ borderColor: "var(--border)" }}
            data-testid={`pref-row-${channel.key}`}
          >
            <div>
              <div className="font-medium" style={{ color: "var(--ink)" }}>{channel.title}</div>
              <p className="mt-1 text-sm max-w-lg" style={{ color: "var(--muted)" }}>{channel.body}</p>
            </div>
            <Switch
              checked={Boolean(prefs[channel.key])}
              disabled={saving}
              onCheckedChange={(value) => toggle(channel.key, value)}
              aria-label={channel.title}
              data-testid={`pref-${channel.key}`}
            />
          </div>
        ))}
      </div>

      <div className="mt-8 border p-6 flex items-start gap-3" style={{ borderColor: "var(--border)", background: "#F3F3F1" }}>
        <Bell size={16} style={{ color: "var(--muted)" }}/>
        <div className="text-sm" style={{ color: "var(--muted)" }} data-testid="settings-portal-note">
          Portal notices stay on for every update — check the bell in the top bar.
        </div>
      </div>

      <div className="mt-4 border p-6 flex items-start gap-3" style={{ borderColor: "var(--border)" }}>
        <ShieldCheck size={16} style={{ color: "var(--muted)" }}/>
        <div className="text-sm" style={{ color: "var(--muted)" }} data-testid="settings-security-note">
          Sign-in codes and password recovery emails always go to your registered work email
          (<span className="font-medium" style={{ color: "var(--ink)" }}>{user?.work_email}</span>) and cannot be turned off.
          Contact your program administrator to change that address.
        </div>
      </div>
    </div>
  );
}
